import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCalendar, FiCheckCircle, FiFilter, FiRefreshCw, FiTrash2, FiZap } from 'react-icons/fi';

import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { PageLayout } from '../../components/layout';
import { Table, TableColumn } from '../../components/Table';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getKiosks } from '../../services/KioskService';
import {
  cancelInvoice,
  getKioskBillingInvoices,
  issueMonthlyInvoices,
  issueSetupInvoice,
  markInvoicePaid,
  runKioskBillingOverdueJob,
} from '../../services/KioskBillingService';
import type { Kiosk } from '../../types/kiosk.type';
import type {
  BillingActionResponse,
  KioskBillingInvoice,
  KioskBillingInvoiceStatus,
  KioskBillingInvoiceType,
} from '../../types/kioskBilling.types';
import { formatDate } from '../../utils/dateFormatter';

const STATUS_OPTIONS: Array<KioskBillingInvoiceStatus | ''> = ['', 'DRAFT', 'ISSUED', 'PAID', 'OVERDUE', 'CANCELLED'];
const TYPE_OPTIONS: Array<KioskBillingInvoiceType | ''> = ['', 'SETUP', 'MONTHLY'];

const money = (value: number, currencyCode = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((value || 0) / 100);
  } catch {
    return `${currencyCode} ${((value || 0) / 100).toFixed(2)}`;
  }
};

const statusVariant: Record<KioskBillingInvoiceStatus, 'default' | 'success' | 'warning' | 'error'> = {
  DRAFT: 'default',
  ISSUED: 'warning',
  PAID: 'success',
  OVERDUE: 'error',
  CANCELLED: 'default',
};

type ActionState =
  | { kind: 'mark-paid'; invoice: KioskBillingInvoice }
  | { kind: 'cancel'; invoice: KioskBillingInvoice }
  | null;

const KioskBillingPage: React.FC = () => {
  const [invoices, setInvoices] = useState<KioskBillingInvoice[]>([]);
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingKiosks, setLoadingKiosks] = useState(false);
  const [pageError, setPageError] = useState('');
  const [kioskFilter, setKioskFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<KioskBillingInvoiceStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<KioskBillingInvoiceType | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [setupKioskId, setSetupKioskId] = useState('');
  const [setupNotes, setSetupNotes] = useState('');
  const [monthlyKioskId, setMonthlyKioskId] = useState('');
  const [monthlyYear, setMonthlyYear] = useState('');
  const [monthlyMonth, setMonthlyMonth] = useState('');
  const [monthlyNotes, setMonthlyNotes] = useState('');
  const [issueLoading, setIssueLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<ActionState>(null);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [monthlyModalOpen, setMonthlyModalOpen] = useState(false);

  const loadKiosks = useCallback(async () => {
    setLoadingKiosks(true);
    try {
      const data = await getKiosks();
      setKiosks(data);
      if (!setupKioskId && data[0]?.kioskId) {
        setSetupKioskId(data[0].kioskId);
      }
      if (!monthlyKioskId && data[0]?.kioskId) {
        setMonthlyKioskId(data[0].kioskId);
      }
    } catch (error) {
      const message = (error as Error).message || 'Failed to load kiosks';
      setPageError(message);
      showErrorToast(message);
    } finally {
      setLoadingKiosks(false);
    }
  }, [monthlyKioskId, setupKioskId]);

  const loadInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    setPageError('');
    try {
      const data = await getKioskBillingInvoices({
        kioskId: kioskFilter || undefined,
        status: statusFilter,
        invoiceType: typeFilter,
        overdueOnly,
      });
      setInvoices(data);
    } catch (error) {
      const message = (error as Error).message || 'Failed to load invoices';
      setPageError(message);
      showErrorToast(message);
    } finally {
      setLoadingInvoices(false);
    }
  }, [kioskFilter, overdueOnly, statusFilter, typeFilter]);

  useEffect(() => {
    void loadKiosks();
    void loadInvoices();
  }, []);

  const kioskOptions = useMemo(
    () => kiosks.map((kiosk) => ({ value: kiosk.kioskId, label: `${kiosk.name || kiosk.kioskId} (${kiosk.kioskId})` })),
    [kiosks],
  );

  const activeKiosk = useMemo(
    () => kiosks.find((item) => item.kioskId === setupKioskId || item.kioskId === monthlyKioskId),
    [kiosks, setupKioskId, monthlyKioskId],
  );

  const handleActionResponse = useCallback((response: BillingActionResponse) => {
    showSuccessToast(response.message || 'Action completed');
    void loadInvoices();
    void loadKiosks();
  }, [loadInvoices, loadKiosks]);

  const issueSetup = async () => {
    if (!setupKioskId) {
      showErrorToast('Select a kiosk for setup invoicing.');
      return;
    }

    setIssueLoading(true);
    try {
      const response = await issueSetupInvoice({ kioskId: setupKioskId, notes: setupNotes.trim() || undefined });
      handleActionResponse(response);
      setSetupNotes('');
      setSetupModalOpen(false);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to issue setup invoice');
    } finally {
      setIssueLoading(false);
    }
  };

  const issueMonthly = async () => {
    setIssueLoading(true);
    try {
      const year = monthlyYear.trim() ? Number(monthlyYear) : undefined;
      const month = monthlyMonth.trim() ? Number(monthlyMonth) : undefined;
      const response = await issueMonthlyInvoices({
        kioskId: monthlyKioskId.trim() || undefined,
        year: Number.isFinite(year as number) ? year : undefined,
        month: Number.isFinite(month as number) ? month : undefined,
        notes: monthlyNotes.trim() || undefined,
      });
      handleActionResponse(response);
      setMonthlyNotes('');
      setMonthlyModalOpen(false);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to issue monthly invoices');
    } finally {
      setIssueLoading(false);
    }
  };

  const runBillingJob = async () => {
    setJobLoading(true);
    try {
      const response = await runKioskBillingOverdueJob();
      handleActionResponse(response);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to run billing job');
    } finally {
      setJobLoading(false);
    }
  };

  const executePendingAction = async () => {
    if (!pendingAction) {
      return;
    }

    setActionLoadingId(pendingAction.invoice.id);
    try {
      if (pendingAction.kind === 'mark-paid') {
        const response = await markInvoicePaid(pendingAction.invoice.id, {
          notes: pendingAction.invoice.notes || undefined,
        });
        handleActionResponse(response);
      }

      if (pendingAction.kind === 'cancel') {
        const response = await cancelInvoice(pendingAction.invoice.id, {
          reason: pendingAction.invoice.cancelledReason || 'Cancelled by admin',
          notes: pendingAction.invoice.notes || undefined,
        });
        handleActionResponse(response);
      }
    } catch (error) {
      showErrorToast((error as Error).message || 'Action failed');
    } finally {
      setPendingAction(null);
      setActionLoadingId(null);
    }
  };

  const columns: TableColumn<KioskBillingInvoice>[] = [
    {
      id: 'invoice',
      label: 'Invoice',
      width: '18%',
      priority: 'high',
      format: (_, row) => (
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 dark:text-gray-100">#{row.id}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{row.kioskName || row.kioskId}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{row.kioskId}</div>
        </div>
      ),
    },
    {
      id: 'invoiceType',
      label: 'Type',
      width: '8%',
      priority: 'medium',
      format: (value) => <Badge label={String(value)} variant="info" />,
    },
    {
      id: 'status',
      label: 'Status',
      width: '10%',
      priority: 'high',
      format: (value) => <Badge label={String(value)} variant={statusVariant[value as KioskBillingInvoiceStatus]} />,
    },
    {
      id: 'ownerEmail',
      label: 'Owner',
      width: '14%',
      priority: 'medium',
      format: (value) => value ? <span className="text-sm">{String(value)}</span> : <span>-</span>,
    },
    {
      id: 'totalCents',
      label: 'Total',
      width: '10%',
      priority: 'high',
      align: 'right',
      format: (_, row) => <span className="font-semibold">{money(row.totalCents, row.currencyCode)}</span>,
    },
    {
      id: 'period',
      label: 'Billing Period',
      width: '16%',
      priority: 'low',
      format: (_, row) => (
        <div className="text-xs text-gray-600 dark:text-gray-300">
          <div>{row.billingPeriodStart ? formatDate(row.billingPeriodStart) : '-'}</div>
          <div>{row.billingPeriodEnd ? formatDate(row.billingPeriodEnd) : '-'}</div>
        </div>
      ),
    },
    {
      id: 'dueAt',
      label: 'Due',
      width: '12%',
      priority: 'medium',
      format: (value) => (value ? formatDate(String(value)) : '-'),
    },
    {
      id: 'createdAt',
      label: 'Created',
      width: '12%',
      priority: 'low',
      format: (value) => (value ? formatDate(String(value)) : '-'),
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '20%',
      priority: 'high',
      type: 'actions',
      format: (_, row) => (
        <div className="flex flex-wrap items-center gap-2">
          {row.status === 'ISSUED' || row.status === 'OVERDUE' ? (
            <Button
              size="sm"
              variant="secondary"
              startIcon={<FiCheckCircle />}
              loading={actionLoadingId === row.id && pendingAction?.kind === 'mark-paid'}
              onClick={() => setPendingAction({ kind: 'mark-paid', invoice: row })}
            >
              Mark Paid
            </Button>
          ) : null}

          {row.status !== 'PAID' && row.status !== 'CANCELLED' ? (
            <Button
              size="sm"
              variant="tertiary"
              startIcon={<FiTrash2 />}
              loading={actionLoadingId === row.id && pendingAction?.kind === 'cancel'}
              onClick={() => setPendingAction({ kind: 'cancel', invoice: row })}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const filters = (
    <div className="rounded-2xl border border-white/60 bg-white/80 p-4 dark:border-white/10 dark:bg-slate-900/70">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Kiosk</label>
          <select
            value={kioskFilter}
            onChange={(event) => setKioskFilter(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
          >
            <option value="">All kiosks</option>
            {kioskOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as KioskBillingInvoiceStatus | '')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status || 'all'} value={status}>
                {status || 'All statuses'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Type</label>
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value as KioskBillingInvoiceType | '')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
          >
            {TYPE_OPTIONS.map((type) => (
              <option key={type || 'all'} value={type}>
                {type || 'All types'}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(event) => setOverdueOnly(event.target.checked)}
            />
            Overdue only
          </label>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<FiFilter />}
            loading={loadingInvoices}
            onClick={loadInvoices}
          >
            Filter
          </Button>
        </div>
      </div>

      {pageError && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {pageError}
        </div>
      )}
    </div>
  );

  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="primary"
        startIcon={<FiCheckCircle />}
        onClick={() => setSetupModalOpen(true)}
      >
        Setup Invoice
      </Button>
      <Button
        variant="primary"
        startIcon={<FiCalendar />}
        onClick={() => setMonthlyModalOpen(true)}
      >
        Monthly Invoice(s)
      </Button>
      <Button variant="secondary" startIcon={<FiZap />} loading={jobLoading} onClick={() => { void runBillingJob(); }}>
        Run Overdue Job
      </Button>
      <Button variant="secondary" startIcon={<FiRefreshCw />} loading={loadingInvoices || loadingKiosks} onClick={() => { void loadInvoices(); void loadKiosks(); }}>
        Reload
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Kiosk Billing"
      subtitle="Issue setup invoices, generate monthly billing, mark payments, and block overdue kiosks"
      actions={actions}
      filters={filters}
    >
      <div className="space-y-6 p-6">
        <div className="rounded-2xl border border-white/60 bg-white/80 p-4 dark:border-white/10 dark:bg-slate-900/70">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invoice List</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">{activeKiosk ? `Focused on ${activeKiosk.name || activeKiosk.kioskId}` : 'All invoices'}</div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{invoices.length} invoices</div>
          </div>

          <Table<KioskBillingInvoice>
            data={invoices}
            columns={columns}
            loading={loadingInvoices}
            emptyMessage="No invoices found"
            striped
            hoverable
          />
        </div>

      </div>

      <ConfirmModal
        open={Boolean(pendingAction)}
        title={pendingAction?.kind === 'mark-paid' ? 'Mark invoice paid?' : 'Cancel invoice?'}
        message={
          pendingAction?.kind === 'mark-paid'
            ? `Confirm payment for invoice #${pendingAction?.invoice?.id} (${pendingAction?.invoice?.kioskId})?`
            : `Cancel invoice #${pendingAction?.invoice?.id} (${pendingAction?.invoice?.kioskId})?`
        }
        body={
          pendingAction ? (
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <div>Total: {money(pendingAction.invoice.totalCents, pendingAction.invoice.currencyCode)}</div>
              <div>Status: {pendingAction.invoice.status}</div>
            </div>
          ) : null
        }
        confirmLabel={pendingAction?.kind === 'mark-paid' ? 'Mark Paid' : 'Cancel Invoice'}
        confirmVariant={pendingAction?.kind === 'mark-paid' ? 'primary' : 'secondary'}
        onClose={() => setPendingAction(null)}
        onConfirm={() => {
          void executePendingAction();
        }}
      />

      {setupModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => setSetupModalOpen(false)}>
          <div
            className="w-full max-w-2xl rounded-2xl border border-white/70 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <FiCheckCircle /> Issue Setup Invoice
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Kiosk</label>
                <select
                  value={setupKioskId}
                  onChange={(event) => setSetupKioskId(event.target.value)}
                  disabled={loadingKiosks}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                >
                  <option value="">Select kiosk</option>
                  {kioskOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <input
                  type="text"
                  value={setupNotes}
                  onChange={(event) => setSetupNotes(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                  placeholder="Optional note"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="tertiary" onClick={() => setSetupModalOpen(false)}>Cancel</Button>
              <Button onClick={() => { void issueSetup(); }} loading={issueLoading} startIcon={<FiCalendar />}>
                Issue Setup Invoice
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {monthlyModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => setMonthlyModalOpen(false)}>
          <div
            className="w-full max-w-3xl rounded-2xl border border-white/70 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <FiZap /> Issue Monthly Invoice(s)
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Kiosk (optional)</label>
                <select
                  value={monthlyKioskId}
                  onChange={(event) => setMonthlyKioskId(event.target.value)}
                  disabled={loadingKiosks}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                >
                  <option value="">All kiosks</option>
                  {kioskOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Year</label>
                <input
                  type="number"
                  value={monthlyYear}
                  onChange={(event) => setMonthlyYear(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                  placeholder="UTC year"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Month</label>
                <input
                  type="number"
                  value={monthlyMonth}
                  onChange={(event) => setMonthlyMonth(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                  placeholder="1-12"
                  min={1}
                  max={12}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <input
                  type="text"
                  value={monthlyNotes}
                  onChange={(event) => setMonthlyNotes(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                  placeholder="Optional note"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="tertiary" onClick={() => setMonthlyModalOpen(false)}>Cancel</Button>
              <Button onClick={() => { void issueMonthly(); }} loading={issueLoading} startIcon={<FiCalendar />}>
                Issue Monthly Invoice(s)
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </PageLayout>
  );
};

export default KioskBillingPage;